import {
  DROPBOX_PICKER,
  MEDIA_UPLOAD_ALL_EXTENSIONS,
  MEDIA_UPLOAD_IMAGE_ONLY_EXTENSIONS
} from '../../../config/constants';
import {
  fetchBrowserFileFromUrl,
  inferMimeFromFilename
} from '../cloudPickerFileUtils';
import {
  CloudPickerAdapter,
  CloudPickerFile,
  LaunchCloudPickerOptions
} from './types';

declare global {
  interface Window {
    Dropbox?: {
      choose: (options: Record<string, unknown>) => void;
    };
  }
}

interface DropboxChooserFile {
  bytes?: number;
  isDir?: boolean;
  link?: string;
  mimeType?: string;
  name?: string;
  icon?: string;
  thumbnailLink?: string;
}

interface DropboxPickerConfig {
  appKey: string;
}

const getAllowedExtensions = (allowVideoAndPdf: boolean) =>
  allowVideoAndPdf
    ? MEDIA_UPLOAD_ALL_EXTENSIONS
    : MEDIA_UPLOAD_IMAGE_ONLY_EXTENSIONS;

const loadDropboxSdk = async (appKey: string) => {
  if (!appKey) {
    throw new Error('Missing Dropbox App Key');
  }

  return await new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(
      DROPBOX_PICKER.scriptId
    ) as HTMLScriptElement | null;

    if (existingScript) {
      if (window.Dropbox) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load Dropbox SDK')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = DROPBOX_PICKER.sdkSrc;
    script.id = DROPBOX_PICKER.scriptId;
    script.setAttribute('data-app-key', appKey);
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Dropbox SDK'));
    document.body.appendChild(script);
  });
};

const launchDropboxPicker = async (
  { allowVideoAndPdf }: LaunchCloudPickerOptions,
  { appKey }: DropboxPickerConfig
): Promise<CloudPickerFile[]> => {
  await loadDropboxSdk(appKey);

  const dropbox = window.Dropbox;
  if (!dropbox) {
    throw new Error('Dropbox SDK unavailable');
  }

  const allowedExtensions = getAllowedExtensions(allowVideoAndPdf);

  return await new Promise<CloudPickerFile[]>((resolve) => {
    dropbox.choose({
      linkType: 'direct',
      multiselect: true,
      folderselect: false,
      extensions: allowedExtensions,
      success: (files: DropboxChooserFile[]) => {
        console.info('[Dropbox] picked files:', files);
        const pickedFiles: CloudPickerFile[] = (files || [])
          .filter((file) => file?.link && !file?.isDir)
          .map((file, index) => {
            const resolvedMime =
              file.mimeType || inferMimeFromFilename(file.name);
            const downloadUrl = file.link || '';
            return {
              provider: 'dropbox' as const,
              name: file.name,
              size: file.bytes,
              mimeType: resolvedMime,
              thumbnailUrl: file.thumbnailLink || file.icon,
              getUploadDescriptor: async () => ({
                url: downloadUrl
              }),
              toBrowserFile: async (onProgress) =>
                await fetchBrowserFileFromUrl(
                  downloadUrl,
                  undefined,
                  {
                    providedName: file.name,
                    mimeType: resolvedMime,
                    fallbackBaseName: `dropbox-file-${Date.now()}-${index + 1}`
                  },
                  onProgress
                )
            };
          });

        resolve(pickedFiles);
      },
      cancel: () => {
        resolve([]);
      }
    });
  });
};

export const createDropboxPicker = (
  config: DropboxPickerConfig
): CloudPickerAdapter => ({
  preload: async () => await loadDropboxSdk(config.appKey),
  launch: async (options) => await launchDropboxPicker(options, config),
  getErrorTranslationKey: () =>
    'lomavisCreator.additionalOptions.cloudImportFailed'
});
